import React from 'react';
import { useHistory } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { combineClassNames, getMediaPath, urlEncodeString } from 'app/functions';
import { IconHot } from 'components/common/IconHot';
import * as routes from 'constants/routes';
import CalendarIconSvg from 'assets/personal_zone/Calendar.svg';
import ChevronLeftIcon from 'assets/icons/slider-arrow-blue.svg';
import benefitPlaceholder from 'assets/design/benefit_placeholder.svg';

const TABLET_UP = '@media (min-width: 768px)';

const CardButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'inline',
})(({ theme, inline }) => ({
    '&&': {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        padding: theme.spacing(1.5, 1.5),
        background: theme.palette.common.white,
        cursor: 'pointer',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.caption.fontSize,
        lineHeight: 1.2,
        textAlign: 'inherit',
        flex: '0 0 auto',
        scrollSnapAlign: 'start',
        boxSizing: 'border-box',
        border: `${theme.spacing(0.125)} solid #E5E5E5`,
        borderRadius: theme.spacing(0.5),
        ...(inline && {
            minHeight: 0,
            height: 'auto',
            padding: theme.spacing(1, 1.25),
            gap: theme.spacing(1),
        }),
        [TABLET_UP]: inline ? {
            minHeight: 54,
            height: 'auto',
            padding: theme.spacing(1.25, 1.25),
            gap: theme.spacing(1.125),
        } : {},
        [theme.breakpoints.up('md')]: inline ? {
            minHeight: 58,
            height: 'auto',
            padding: theme.spacing(1.375, 1.25),
            gap: theme.spacing(1.25),
        } : {
            gap: theme.spacing(1),
        },
        [theme.breakpoints.up('lg')]: inline ? {
            minHeight: 60,
            padding: theme.spacing(1.5, 1.25),
        } : {},
    },
}));

const CardImage = styled('span', {
    shouldForwardProp: (prop) => prop !== 'inline',
})(({ theme, inline }) => ({
    '&&': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '44px',
        height: '44px',
        minWidth: '44px',
        flexShrink: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: theme.palette.common.white,
        borderRadius: theme.spacing(0.375),
        [TABLET_UP]: {
            width: inline ? theme.spacing(5.5) : theme.spacing(4),
            height: inline ? theme.spacing(5.5) : theme.spacing(4),
            minWidth: inline ? theme.spacing(5.5) : theme.spacing(4),
        },
        [theme.breakpoints.up('md')]: inline ? {
            width: theme.spacing(6),
            height: theme.spacing(6),
            minWidth: theme.spacing(6),
        } : {},
        '& img': {
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
        },
    },
}));

const CardContent = styled('span')(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    border: `${theme.spacing(0.125)} solid ${theme.palette.border.divider}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5),
}));

const CardTextColumn = styled('span')(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.spacing(0.25),
    textAlign: 'left',
}));

const CardName = styled('span', {
    shouldForwardProp: (prop) => prop !== 'inline',
})(({ theme, inline }) => ({
    '&&': {
        minWidth: 0,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.pxToRem(16),
        fontWeight: theme.typography.fontWeightBold,
        lineHeight: 1.3,
        color: theme.palette.text.primary,
        unicodeBidi: 'plaintext',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...(inline
            ? { display: 'block', width: '100%', textAlign: 'left' }
            : { flex: '1 1 auto', justifyContent: 'center', textAlign: 'center' }),
        [TABLET_UP]: inline ? {
            fontSize: theme.typography.pxToRem(15),
            lineHeight: 1.3,
        } : {
            fontSize: theme.typography.caption.fontSize,
        },
        [theme.breakpoints.up('md')]: inline ? {
            fontSize: theme.typography.pxToRem(16),
            lineHeight: 1.3,
        } : {},
        [theme.breakpoints.up('lg')]: inline ? {
            fontSize: theme.typography.pxToRem(16),
        } : {},
    },
}));

const CardDate = styled('span', {
    shouldForwardProp: (prop) => prop !== 'inline',
})(({ theme, inline }) => ({
    '&&': {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: inline ? 'flex-start' : 'auto',
        gap: theme.spacing(0.375),
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeightRegular,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.35,
        color: theme.palette.brands.text.inactive,
        direction: 'ltr',
        unicodeBidi: 'plaintext',
        flexShrink: 0,
        '& span': {
            fontSize: 'inherit',
            lineHeight: 'inherit',
            color: theme.palette.text.primary,
        },
        '& img, & svg': {
            width: theme.typography.pxToRem(13),
            height: theme.typography.pxToRem(13),
            flexShrink: 0,
        },
        [TABLET_UP]: inline ? {
            fontSize: theme.typography.pxToRem(13),
            lineHeight: 1.35,
            color: theme.palette.brands.text.inactive,
            '& img, & svg': {
                width: theme.typography.pxToRem(12),
                height: theme.typography.pxToRem(12),
            },
        } : {
            fontSize: theme.typography.caption.fontSize,
            lineHeight: 1.35,
            color: theme.palette.brands.text.inactive,
        },
        [theme.breakpoints.up('md')]: inline ? {
            fontSize: theme.typography.pxToRem(14),
            lineHeight: 1.35,
            color: theme.palette.text.primary,
            '& img, & svg': {
                width: theme.typography.pxToRem(13),
                height: theme.typography.pxToRem(13),
            },
        } : {},
    },
}));

const CardChevron = styled('img')(({ theme }) => ({
    width: '11px',
    height: 'auto',
    flexShrink: 0,
    opacity: 0.6,
    color: '#7E7DBF99',
}));

const getBenefitImage = (benefit) =>
    benefit?.logoUrl ?? benefit?.image
    ?? benefitPlaceholder;

export const HotYoterPurchasedBenefitCard = ({
    benefit,
    showOrderDate = true,
    inline = false,
    testID = 'hot-yoter-purchased-benefit-card',
}) => {
    const history = useHistory();

    const handlePress = () => {
        if (!benefit?.id) {
            return;
        }

        history.push(
            `${routes.benefit}/${benefit.id}/${urlEncodeString(benefit.name)}`
        );
    };

    const calendarIconSize = inline ? 12 : 13;

    const nameBlock = benefit?.name ? (
        <CardName
            className="hot-yoter-purchased-benefit-card__name"
            inline={inline}
            testId={`${testID}-name`}
        >
            {benefit.name}
        </CardName>
    ) : null;

    const dateBlock = benefit?.orderDate && showOrderDate ? (
        <CardDate className="hot-yoter-purchased-benefit-card__date" inline={inline}>
            <IconHot src={CalendarIconSvg} size={calendarIconSize} />
            <span testId={`${testID}-order-date`}>
                {benefit.orderDate}
            </span>
        </CardDate>
    ) : null;

    return (
        <CardButton
            type="button"
            className={combineClassNames(
                'hot-yoter-purchased-benefit-card',
                inline && 'hot-yoter-purchased-benefit-card_inline',
            )}
            inline={inline}
            onClick={handlePress}
            testId={testID}
            dir="rtl"
        >
            <CardImage className="hot-yoter-purchased-benefit-card__image" inline={inline}>
                <img src={getMediaPath(getBenefitImage(benefit))} alt="" />
            </CardImage>

            {inline ? (
                <CardTextColumn className="hot-yoter-purchased-benefit-card__text">
                    {nameBlock}
                    {dateBlock}
                </CardTextColumn>
            ) : (
                <CardContent className="hot-yoter-purchased-benefit-card__content">
                    {nameBlock}
                    {dateBlock}
                </CardContent>
            )}

            <CardChevron
                className="hot-yoter-purchased-benefit-card__chevron"
                src={getMediaPath(ChevronLeftIcon)}
                alt=""
                testId={`${testID}-chevron`}
            />
        </CardButton>
    );
};
